import { Icon } from '@iconify/react';
import { useCallback, useEffect, useState } from 'react';
// material
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip
} from '@mui/material';
// other icons
import searchFill from '@iconify/icons-eva/search-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { useDispatch } from 'react-redux';
import { RootStyle, SearchStyle } from '../Global/Styling';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import {
  getCityData,
  getLoanTypeData,
  getSourceListData,
  getStatusList
} from '../../store/Slice/Global/GlobalSlice';
import useRejectedSelector from '../../store/Slice/Rejected/RejectedSelector';

export default function RejectedToolbar({
  filterName,
  onFilterName,
  filterDatesRange,
  setFilterDatesRange,
  filterState,
  setFilterState,
  setFilterName
}) {
  const [isOpenFilter, openFilter] = useState(false);
  const [uniqueReasonOptions, setUniqueReasonOptions] = useState([]);

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { CityDrop, LoanTypeDrop, BankDrop } = useGlobalSelector();
  const { RejectedListData } = useRejectedSelector();

  const handleRemoveFilter = () => {
    setFilterDatesRange([
      {
        startDate: null,
        endDate: null,
        key: 'selection'
      }
    ]);
    setFilterState((prev) => ({
      ...prev,
      bankId: null,
      loanTypeId: null,
      product: ' ',
      productId: null,
      city: null,
      cityId: null,
      status: ' ',
      statusId: null,
      rejectedReasonTypeId: null,
      reason: null
    }));
    setFilterName('');
  };

  const getLoanMasterApi = useCallback(() => {
    const obj = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getLoanTypeData(obj));
  }, [LoginData, dispatch]);

  const handleGetState = useCallback(
    (optionVal) => {
      const data = {
        ...(optionVal && { stateId: optionVal }),
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,
        searchLiteral: '',
        sortOrder: 'desc'
      };
      dispatch(getCityData(data));
    },
    [LoginData, dispatch]
  );

  useEffect(() => {
    getLoanMasterApi();
    handleGetState();
  }, [getLoanMasterApi, handleGetState]);

  useEffect(() => {
    dispatch(getStatusList(58));
    dispatch(getSourceListData());
  }, [dispatch]);

  useEffect(() => {
    if (!RejectedListData?.length || uniqueReasonOptions.length > 0) return;

    const uniqueData = RejectedListData.filter(
      (r, idx, self) => self.findIndex((x) => x?.reason === r?.reason) === idx
    ).map((r) => ({
      label: r?.reason,
      value: r?.reasonId
    }));

    setUniqueReasonOptions(uniqueData);
  }, [RejectedListData]);

  return (
    <>
      {isOpenFilter && (
        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <Select
                fullWidth
                size="small"
                value={filterState.bankId ?? ' '}
                onChange={(e) => {
                  setFilterState((prev) => ({ ...prev, bankId: e.target.value }));
                }}
                sx={{ borderRadius: '8px', backgroundColor: '#ffffff', fontSize: '13px' }}
              >
                <MenuItem value=" " disabled>
                  Select Bank
                </MenuItem>
                {BankDrop?.map((productItem) => (
                  <MenuItem key={productItem?.value} value={productItem?.value}>
                    {productItem.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Select
                fullWidth
                size="small"
                value={filterState.loanTypeId ?? ' '}
                onChange={(e) => {
                  setFilterState((prev) => ({ ...prev, loanTypeId: e.target.value }));
                }}
                sx={{ borderRadius: '8px', backgroundColor: '#ffffff', fontSize: '13px' }}
              >
                <MenuItem value=" " disabled>
                  Select Product Type
                </MenuItem>
                {LoanTypeDrop?.map((productItem) => (
                  <MenuItem key={productItem?.value} value={productItem?.value}>
                    {productItem.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                disablePortal
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder="Select City"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        fontSize: '13px'
                      }
                    }}
                  />
                )}
                value={filterState.city ?? null}
                options={CityDrop || []}
                getOptionLabel={(option) => option?.label ?? ''}
                renderOption={(props, option) => <li {...props}>{option?.label ?? ''}</li>}
                onChange={(e, newValue) => {
                  setFilterState((prev) => ({
                    ...prev,
                    city: newValue ?? null,
                    cityId: newValue ? newValue.value : null
                  }));
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.5}>
              <Select
                fullWidth
                size="small"
                value={filterState.rejectedReasonTypeId ?? ' '}
                onChange={(e) => {
                  setFilterState((prev) => ({ ...prev, rejectedReasonTypeId: e.target.value }));
                }}
                sx={{ borderRadius: '8px', backgroundColor: '#ffffff', fontSize: '13px' }}
              >
                <MenuItem value=" " disabled>
                  Select Rejection Reason
                </MenuItem>
                {uniqueReasonOptions.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs="auto">
              <Tooltip title="Reset Filters" placement="top" arrow>
                <IconButton
                  onClick={handleRemoveFilter}
                  sx={{
                    color: '#ef4444',
                    backgroundColor: '#fee2e2',
                    borderRadius: '8px',
                    p: 0.75,
                    '&:hover': { backgroundColor: '#fecaca' }
                  }}
                >
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      )}

      <RootStyle
        sx={{
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1.5,
          p: { xs: 2, sm: 2.5 },
          bgcolor: '#ffffff'
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <TextField
            size="small"
            value={filterName}
            onChange={onFilterName}
            placeholder="Search Rejection..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon={searchFill} style={{ color: '#94a3b8', fontSize: 18 }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: { xs: '100%', sm: 260 },
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff',
                borderRadius: '10px',
                fontSize: '13px',
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#0284c7' }
              }
            }}
          />

          <Button
            variant="outlined"
            onClick={() => openFilter((prev) => !prev)}
            startIcon={<Icon icon={roundFilterList} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: isOpenFilter ? '#0284c7' : '#475569',
              borderColor: isOpenFilter ? '#0284c7' : '#e2e8f0',
              bgcolor: isOpenFilter ? 'rgba(2, 132, 199, 0.06)' : '#fff',
              height: 40,
              px: 2,
              '&:hover': {
                borderColor: '#0284c7',
                bgcolor: 'rgba(2, 132, 199, 0.06)',
                color: '#0284c7'
              }
            }}
          >
            Filters
          </Button>
        </Box>
      </RootStyle>
    </>
  );
}
